import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { CopyButton } from './CopyButton.js'

export interface MessageTemplatesProps {
  messageEn: string
  messageTl: string
  setupLink: string
}

type Tab = 'en' | 'tl'

/**
 * The EN / Tagalog tabbed message preview with copy buttons and the raw
 * setup link. Reused inline in the just-created panel and inside
 * MessageTemplatesModal for the regenerate-link flow.
 */
export function MessageTemplates({
  messageEn,
  messageTl,
  setupLink,
}: MessageTemplatesProps) {
  const [tab, setTab] = useState<Tab>('en')
  const active = tab === 'en' ? messageEn : messageTl

  return (
    <div>
      <div className="admin-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'en'}
          className={`admin-tab ${tab === 'en' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('en')}
        >
          English
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tl'}
          className={`admin-tab ${tab === 'tl' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('tl')}
        >
          Tagalog
        </button>
      </div>

      <div className="admin-msg-block" data-testid="message-body">
        {active}
      </div>
      <div style={{ marginBottom: 16 }}>
        <CopyButton value={active} label="Copy message" />
      </div>

      <label className="admin-label">Setup link</label>
      <div className="admin-copy-row">
        <input
          className="admin-input"
          readOnly
          value={setupLink}
          onFocus={(e) => e.currentTarget.select()}
        />
        <CopyButton value={setupLink} label="Copy link" />
      </div>

      {/* QR of the same setup link — lets the admin point the owner's phone
          camera at the screen instead of sending the long URL over chat.
          The owner scans it and lands directly in the setup wizard. */}
      <div className="admin-setup-qr">
        <QRCodeSVG value={setupLink} size={180} level="M" includeMargin />
        <p className="admin-hint admin-setup-qr-caption">
          Or have the owner scan this with their phone camera to open the
          setup wizard directly.
        </p>
      </div>
    </div>
  )
}

interface MessageTemplatesModalProps extends MessageTemplatesProps {
  title: string
  onClose: () => void
}

export function MessageTemplatesModal({
  title,
  onClose,
  ...templates
}: MessageTemplatesModalProps) {
  return (
    <div
      className="admin-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-page-head">
          <h3>{title}</h3>
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <MessageTemplates {...templates} />
      </div>
    </div>
  )
}
